import { INK, MUTED, LINE, NEUTRAL_BG, WHITE, CULTURE_COLORS, text } from "./constants";
import { useIsMobile } from "./lib/responsive";
import { CULTURE_DIMS } from "./lib/culture";

// Metodología del perfil de cultura (30/10, pestaña de «Documento de cultura»).
// Resumida, pero con la base académica a la vista: de dónde sale cada
// dimensión, cómo se calcula y cómo se lee. Si cambian las dimensiones o el
// cálculo de lib/culture.js, se cambia también aquí.

// Cada fuente, con lo que aporta y las dimensiones que salen de ella (ids de CULTURE_DIMS)
const SOURCES = [
  { who: "O'Reilly, Chatman & Caldwell (1991)", where: "Academy of Management Journal",
    title: { es: "Organizational Culture Profile", en: "Organizational Culture Profile" },
    what: { es: "El instrumento de referencia para medir el encaje entre una persona y una organización.", en: "The reference instrument for measuring person–organisation fit." },
    dims: ["standards", "risk"] },
  { who: "Hofstede, Neuijen, Ohayv & Sanders (1990)", where: "Administrative Science Quarterly",
    title: { es: "Dimensiones de la cultura organizacional", en: "Dimensions of organisational culture" },
    what: { es: "Midió la cultura de 20 organizaciones: control estricto o laxo, foco en la tarea o en las personas.", en: "Measured culture across 20 organisations: tight or loose control, task or people focus." },
    dims: ["autonomy", "growth"] },
  { who: "Eisenhardt (1989)", where: "Academy of Management Journal",
    title: { es: "Decisiones rápidas en entornos que cambian", en: "Fast decisions in high-velocity environments" },
    what: { es: "Por qué unas empresas deciden en días y otras en meses, y qué cuesta cada ritmo.", en: "Why some firms decide in days and others in months, and what each pace costs." },
    dims: ["speed"] },
  { who: "Edmondson (1999)", where: "Administrative Science Quarterly",
    title: { es: "Seguridad psicológica", en: "Psychological safety" },
    what: { es: "Cuánto se puede discrepar o decir un error en voz alta sin coste personal.", en: "How freely people can disagree or admit a mistake without personal cost." },
    dims: ["candor"] },
  { who: "Gosling, Rentfrow & Swann (2003)", where: "Journal of Research in Personality",
    title: { es: "Medidas breves (TIPI)", en: "Very brief measures (TIPI)" },
    what: { es: "Demuestra que dos ítems bien elegidos por dimensión bastan para una medida fiable.", en: "Shows that two well-chosen items per dimension are enough for a reliable measure." },
    dims: [] },
];

const STEPS = [
  { es: ["12 escenarios reales", "Dos por dimensión. Cada uno plantea una situación de trabajo con dos formas de resolverla, y se contesta en 4 niveles: de «Claramente A» a «Claramente B»."],
    en: ["12 real scenarios", "Two per dimension. Each sets out a work situation with two ways to handle it, answered on 4 levels: from “Clearly A” to “Clearly B”."] },
  { es: ["Equilibrado contra el sesgo", "En cada dimensión un escenario puntúa alto con A y el otro con B: contestar siempre la misma letra da un 3, no un extremo."],
    en: ["Balanced against bias", "In each dimension one scenario scores high with A and the other with B: always picking the same letter gives a 3, not an extreme."] },
  { es: ["Nota calculada, no interpretada", "Cada dimensión va de 1 a 5 y sale de una fórmula fija. La IA solo redacta el documento; las 2 preguntas abiertas le dan el tono."],
    en: ["Calculated, not interpreted", "Each dimension runs from 1 to 5 and comes from a fixed formula. AI only drafts the document; the 2 open questions give it its voice."] },
];

const CultureMethodology = ({ lang }) => {
  const es = lang === "es";
  const isMobile = useIsMobile();
  const L = (o) => (es ? o.es : o.en);
  const dimName = (id) => { const d = CULTURE_DIMS.find(x => x.id === id); return d ? L(d) : id; };
  // 03/11: va dentro de la caja de la pestaña (DocumentsView), con su título.
  // Apartados con etiqueta, cajas como las de Perfil (12 de radio, 13/800) y texto a 13.
  const card = { background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 12, padding: "12px 14px", minWidth: 0 };
  const h2 = { ...text.label, margin: "0 0 4px" };
  const sub = { ...text.body, color: MUTED, margin: "0 0 12px" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 24 : 28 }}>

      <section>
        <h2 style={h2}>{es ? "Base académica" : "Academic foundation"}</h2>
        <p style={sub}>{es ? "Cinco trabajos de referencia en cultura organizacional y medición." : "Five reference works in organisational culture and measurement."}</p>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          {SOURCES.map(s => (
            <div key={s.who} style={{ ...card, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: INK }}>{L(s.title)}</div>
              <div style={text.body}>{L(s.what)}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 }}>
                {s.dims.length ? s.dims.map(id => (
                  <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: INK, background: NEUTRAL_BG, borderRadius: 999, padding: "3px 9px" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: CULTURE_COLORS[id] }} />{dimName(id)}
                  </span>
                )) : (
                  <span style={{ fontSize: 11, fontWeight: 700, color: INK, background: NEUTRAL_BG, borderRadius: 999, padding: "3px 9px" }}>{es ? "Dos escenarios por dimensión" : "Two scenarios per dimension"}</span>
                )}
              </div>
              <div style={{ ...text.meta, marginTop: "auto", paddingTop: 6 }}>{s.who} · <i>{s.where}</i></div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 style={h2}>{es ? "Cómo se calcula" : "How it is scored"}</h2>
        <p style={sub}>{es ? "Determinista de principio a fin." : "Deterministic end to end."}</p>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
          {STEPS.map((s, i) => {
            const [t, d] = L(s);
            return (
              <div key={i} style={{ ...card, display: "flex", gap: 12 }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", background: NEUTRAL_BG, color: INK, fontSize: 12, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: INK, marginBottom: 4 }}>{t}</div>
                  <div style={text.body}>{d}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 style={h2}>{es ? "Cómo se lee" : "How to read it"}</h2>
        <div style={{ ...text.body, maxWidth: 760 }}>
          {es
            ? <>Ningún extremo es mejor: un 1 y un 5 describen dos formas de trabajar, no una buena y una mala. Lo que importa es el <b style={{ color: INK }}>encaje</b> con cada candidato, que se evalúa en el vector <b style={{ color: INK }}>Cultural fit</b> de la evaluación. El documento de cultura traduce el perfil a lenguaje claro para que quien no encaje se autoexcluya antes de la entrevista. <span style={{ color: MUTED }}>Todo esto se suma al resto del método de Rutment: investigación académica, las técnicas de los headhunters de referencia y procesos reales.</span></>
            : <>Neither end is better: a 1 and a 5 describe two ways of working, not a good one and a bad one. What matters is the <b style={{ color: INK }}>fit</b> with each candidate, assessed in the <b style={{ color: INK }}>Cultural fit</b> vector of the evaluation. The culture document turns the profile into plain language so that those who don't fit self-select out before the interview. <span style={{ color: MUTED }}>This sits within Rutment's wider method: academic research, the techniques of top global headhunters, and real hiring processes.</span></>}
        </div>
      </section>
    </div>
  );
};

export default CultureMethodology;
